import type { CadenceStep, ReminderPolicy } from '@/types/reminder-policy';

/** Why a configured step never reaches the customer. */
export type UnreachableReason = 'truncated' | 'voice-off';

/**
 * Which cadence steps the engine will never send, and why.
 *
 * Mirrors `RemindersService.materialize` exactly, INCLUDING its order:
 * `applyChannelPolicy(cadence, policy).slice(0, maxAttempts)`
 * (`reminders.service.ts:244`). VOICE steps are dropped BEFORE the slice when
 * `aiVoiceFallback` is off, so raw cadence index is not the same thing as
 * position in the queue the engine actually builds.
 *
 * Counting on the raw index — which this view did first — gets it wrong twice
 * over on any policy mixing VOICE with the toggle off: it marks trailing steps
 * as dead when the dropped VOICE steps made room for them, and it leaves the
 * VOICE steps themselves showing a send icon they will never honour. Both are
 * the same lie this view exists to stop telling.
 */
export function cadenceReachability(
  policy: Pick<ReminderPolicy, 'cadence' | 'maxAttempts' | 'aiVoiceFallback'>,
): (UnreachableReason | null)[] {
  const gatedOut = (step: CadenceStep) =>
    !policy.aiVoiceFallback && step.channel === 'VOICE';

  const survives = new Set(
    policy.cadence
      .map((step, index) => ({ step, index }))
      .filter(({ step }) => !gatedOut(step))
      .slice(0, policy.maxAttempts)
      .map(({ index }) => index),
  );

  return policy.cadence.map((step, index) => {
    if (survives.has(index)) return null;
    return gatedOut(step) ? 'voice-off' : 'truncated';
  });
}
